import { EntityInEstateStatus, TestamentaryEntityKind } from '@/types/schema';
import { UnreachableError } from '@/utils/errors';
import { englishJoinStringList } from '@/utils/formatting/strings';
import { getNodes } from '@/utils/graphqlUtils';

interface CreatedFromEntitiesConnection {
  edges?: Array<{
    node?: {
      id: string;
      subtype: {
        displayName: string;
      };
    } | null;
  } | null> | null;
}

/**
 * Formats the "Created from" text for testamentary entities.
 * @param createdFromEntities - The connection of entities this TE was created from
 * @returns Formatted string like "Created from Entity A and Entity B", or null if no entities
 */
export function formatCreatedFromEntities(
  createdFromEntities: CreatedFromEntitiesConnection | null | undefined
): string | null {
  const prefix = 'Created from';
  const entities = getNodes(createdFromEntities);
  if (entities.length === 0) return null;

  const entityNames = entities.map((e) => e.subtype.displayName);
  return `${prefix} ${englishJoinStringList(entityNames)}`;
}

export function testamentaryEntityKindToDisplayName(
  testamentaryEntityKind: TestamentaryEntityKind,
  grantorName?: string
): string {
  switch (testamentaryEntityKind) {
    case TestamentaryEntityKind.CharitableEntity:
      return 'Charitable entity';
    case TestamentaryEntityKind.CharitableTrust:
      return 'Charitable trust';
    case TestamentaryEntityKind.GrantorEstate:
      return `Estate of ${grantorName || 'grantor'}`;
    case TestamentaryEntityKind.InheritedRetirementAccount:
      return 'Inherited retirement account';
    case TestamentaryEntityKind.TestamentaryTrust:
      return 'Sub-trust or testamentary trust';

    default:
      throw new UnreachableError({
        case: testamentaryEntityKind,
        message: `Unknown testamentary entity kind ${testamentaryEntityKind}`,
      });
  }
}

export const testamentaryEntityAssetLocationToDisplayName = (
  assetLocation: EntityInEstateStatus | undefined | null
) => {
  switch (assetLocation) {
    case EntityInEstateStatus.InEstate:
      return 'In estate';
    case EntityInEstateStatus.OutOfEstate:
      return 'Out of estate';
    default:
      return '-';
  }
};

export const CHARITABLE_TESTAMENTARY_ENTITY_KINDS: Readonly<
  TestamentaryEntityKind[]
> = [
  TestamentaryEntityKind.CharitableEntity,
  TestamentaryEntityKind.CharitableTrust,
];
